/**
 * API Helper Utility - By John Bellew - Infinia.ie - last updated 15/07/2025
 * 
 * A comprehensive API helper utility with advanced features:
 * - Auth0 authentication token management
 * - Automatic token refresh and retry logic
 * - IndexedDB caching for performance optimization
 * - Multi-format file handling (JSON, PDF, images, videos, audio)
 * - Profile management and media upload capabilities
 * - Qdrant vector database integration
 * - Working group management functionality
 * - Error handling and response processing
 * - Local file fallback support
 * - Media content streaming and caching
 * - Repository file mappings for news articles
 * - Unified file access with backward compatibility
 * 
 * Core Features:
 * - Token-based authentication with Auth0
 * - Automatic token refresh on 401/403 errors
 * - IndexedDB caching with expiration support
 * - File upload and download capabilities
 * - Profile image and video management
 * - Working group updates and member profiles
 * - Qdrant search and indexing operations
 * - News repository file mappings
 * - Media content handling (images, videos, audio)
 * - Local file loading with API fallback
 * - Authentication testing and diagnostics
 * - Response error handling and retries
 * 
 * Authentication Flow:
 * 1. Auth0 token getter initialization
 * 2. Silent token retrieval with retry logic
 * 3. Token caching in IndexedDB
 * 4. Automatic refresh on authentication errors
 * 5. Comprehensive error handling and logging
 * 
 * @requires auth0-react Auth0 integration
 * @requires indexedDB Browser storage for caching
 * @requires fetch Web API for HTTP requests
 * @returns {Object} API helper object with all utility methods
 */
import { setItemInIndexedDB, getItemFromIndexedDB } from './indexedDBHelper';
import localFileHelper from './localFileHelper';
import errorLogger from './errorLogger';
const expiryTime = 3600 * 1000; // 1 hour in milliseconds

let auth0TokenGetter = null;
let loginTimestamp = null; // Track when login occurred

// Helper to check if we're still in the login grace period
const isInLoginGracePeriod = () => {
  if (!loginTimestamp) return false;
  return (Date.now() - loginTimestamp) < 5000; // 5 second grace period
};

// Helper to mark login completion
const markLoginComplete = () => {
  loginTimestamp = Date.now();
};

const isCacheValid = (cacheTime, expiryTime) => {
  return cacheTime && (Date.now() - cacheTime < expiryTime);
};

const apiHelper = {
  setAuth0TokenGetter: (getAccessTokenSilently) => {
    auth0TokenGetter = getAccessTokenSilently;
    // Mark login as complete when Auth0 is initialized
    markLoginComplete();
  },

  // Method to check if we're in login grace period
  isInLoginGracePeriod: () => {
    return isInLoginGracePeriod();
  },

  // Method to manually mark login complete (for testing)
  markLoginComplete: () => {
    markLoginComplete();
  },

  // Diagnostic function to check auth status
  checkAuthStatus: () => {
    return {
      tokenGetterInitialized: !!auth0TokenGetter,
      audience: process.env.REACT_APP_AUTH0_AUDIENCE,
      apiUrl: process.env.REACT_APP_API_URL,
      inLoginGracePeriod: isInLoginGracePeriod()
    };
  },

  // Test function for debugging authentication issues
  testAuth: async () => {
    try {
      // Check basic setup
      const status = apiHelper.checkAuthStatus();
      console.log('Auth Status Check:', status);
      
      if (!status.tokenGetterInitialized) {
        console.error('Auth0 token getter not initialized');
        return { success: false, error: 'Token getter not initialized' };
      }
      
      // Test token retrieval
      console.log('Attempting to get auth token...');
      const token = await apiHelper.getAuthToken();
      
      if (!token) {
        console.error('No token received');
        return { success: false, error: 'No token received' };
      }

      // Validate token format
      const tokenParts = token.split('.');
      console.log('Token validation:', {
        length: token.length,
        parts: tokenParts.length,
        header: tokenParts[0] ? tokenParts[0].substring(0, 20) + '...' : 'missing',
        payload: tokenParts[1] ? tokenParts[1].substring(0, 20) + '...' : 'missing',
        signature: tokenParts[2] ? tokenParts[2].substring(0, 20) + '...' : 'missing'
      });
      
      // Test API call without auth (should work)
      console.log('Testing unprotected endpoint...');
      const response1 = await fetch(`${status.apiUrl}auth-test`);
      const result1 = await response1.json();
      
      // Test API call with auth
      console.log('Testing protected endpoint...');
      const response2 = await fetch(`${status.apiUrl}auth-test`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const result2 = await response2.json();
      
      return { 
        success: true, 
        tokenLength: token.length,
        tokenParts: tokenParts.length,
        unprotectedTest: result1,
        protectedTest: result2
      };
      
    } catch (error) {
      console.error('Authentication test failed:', error);
      return { success: false, error: error.message };
    }
  },

  initialize: async () => {
    try {
      const token = await apiHelper.getAuthToken();
      return token;
    } catch (error) {
      console.error('Failed to initialize API Helper:', error);
      throw error;
    }
  },

  getAuthToken: async () => {
    try {
      // Check if we're in a browser environment
      if (typeof window === 'undefined') {
        throw new Error('Auth token can only be retrieved in browser environment');
      }

      // First, check if we have a cached token that's still valid
      try {
        const cachedToken = await getItemFromIndexedDB('authToken');
        const tokenExpiry = await getItemFromIndexedDB('tokenExpiry');
        
        // Check if token is still valid (with 10-minute buffer for proactive refresh)
        const tenMinutesFromNow = Date.now() + (10 * 60 * 1000);
        
        if (cachedToken && tokenExpiry && tokenExpiry > tenMinutesFromNow) {
          // Validate cached token format
          const tokenParts = cachedToken.split('.');
          if (tokenParts.length === 3) {
            return cachedToken;
          }
        } else if (cachedToken && tokenExpiry && Date.now() < tokenExpiry) {
          // Token is valid but expires soon - trigger proactive refresh in background
          console.log('Token expires soon, triggering proactive refresh');
          // Don't await this - let it refresh in background while we use current token
          setTimeout(() => {
            this.refreshTokenProactively();
          }, 100);
          
          // Return current token for immediate use
          const tokenParts = cachedToken.split('.');
          if (tokenParts.length === 3) {
            return cachedToken;
          }
        }
      } catch (cacheError) {
        console.warn('Error checking cached token:', cacheError);
      }

      let retries = 5; // Increased retries for Auth0 initialization
      while (retries > 0) {
        if (auth0TokenGetter) {
          try {
            const token = await auth0TokenGetter({
              audience: process.env.REACT_APP_AUTH0_AUDIENCE,
            });

            if (!token) {
              console.error('Auth0 returned empty token');
              throw new Error('Empty token received from Auth0');
            }

            // Validate token format (JWT should have 3 parts separated by dots)
            const tokenParts = token.split('.');
            if (tokenParts.length !== 3) {
              console.error('Invalid JWT format received from Auth0:', token.substring(0, 50) + '...');
              throw new Error('Invalid JWT token format');
            }

            // Cache token with 23 hour expiry (leave 1 hour buffer)
            await setItemInIndexedDB('authToken', token);
            await setItemInIndexedDB('tokenExpiry', Date.now() + (23 * 60 * 60 * 1000));

            return token;
          } catch (tokenError) {
            console.warn(`Auth0 token error (${retries} retries left):`, tokenError.message);
            
            // If it's a login required error, don't retry
            if (tokenError.error === 'login_required' || tokenError.error === 'consent_required') {
              throw new Error('User authentication required');
            }
            
            // For other errors, continue with retry logic
            // Don't throw yet, let the retry logic handle it
          }
        } else {
          // More graceful handling when Auth0 is not initialized
          if (retries === 5) {
            console.warn('Auth0 token getter not yet initialized, waiting...');
          }
        }

        retries--;
        if (retries > 0) {
          // Progressive wait time - longer waits for Auth0 initialization
          const waitTime = retries > 3 ? 1000 : retries > 1 ? 2000 : 3000;
          await new Promise(resolve => setTimeout(resolve, waitTime));
        }
      }

      // Instead of throwing immediately, return a specific error that components can handle
      throw new Error('AUTH0_NOT_READY');
    } catch (error) {
      if (error.message === 'AUTH0_NOT_READY') {
        // Don't log this as an error since it's expected during initialization
        throw error;
      }
      console.error('Error in getAuthToken:', error);
      throw error;
    }
  },

  // Proactive token refresh to prevent expiration during polling
  refreshTokenProactively: async function() {
    try {
      if (!auth0TokenGetter) {
        console.warn('Cannot refresh token proactively - Auth0 not initialized');
        return;
      }

      console.log('Proactively refreshing Auth0 token');
      const token = await auth0TokenGetter({
        audience: process.env.REACT_APP_AUTH0_AUDIENCE,
      });

      if (token) {
        // Cache the new token with 23-hour expiry
        await setItemInIndexedDB('authToken', token);
        await setItemInIndexedDB('tokenExpiry', Date.now() + (23 * 60 * 60 * 1000));
        console.log('Token proactively refreshed successfully');
      }
    } catch (error) {
      console.warn('Proactive token refresh failed:', error.message);
      // Don't throw error - this is a background operation
    }
  },

  handleResponse: async (response) => {

    if (response.status === 401 || response.status === 403) {
      // Emit API error event for health monitoring
      window.dispatchEvent(new CustomEvent('apiAuthError', {
        detail: { status: response.status, url: response.url }
      }));
      
      // Clear existing token
      await setItemInIndexedDB('authToken', null);
      await setItemInIndexedDB('tokenExpiry', null);
      // Try to get a new token
      try {
        const newToken = await apiHelper.getAuthToken();
        // Retry the original request with the new token
        const originalRequest = response.url;
        const newResponse = await fetch(originalRequest, {
          headers: {
            'Authorization': `Bearer ${newToken}`,
            'Content-Type': 'application/json'
          }
        });
        if (!newResponse.ok) {
          // Emit error event for the retry failure too
          window.dispatchEvent(new CustomEvent('apiAuthError', {
            detail: { status: newResponse.status, url: newResponse.url }
          }));
          throw new Error('Authentication failed after token refresh');
        }
        return newResponse;
      } catch (error) {
        errorLogger.logAuthError(error, {
          context: 'token_refresh_failed',
          originalStatus: response.status
        });
        throw new Error('Authentication failed - please try again');
      }
    }
    if (!response.ok) {
      const errorMessage = `${response.status} - ${response.statusText}`;
      errorLogger.logApiError(
        response.url,
        new Error(errorMessage),
        { method: 'unknown' },
        response
      );
      throw new Error(errorMessage);
    }
    return response;
  },

  fetchJSONData: async (endpoint) => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      if (!apiUrl) throw new Error('API URL is not defined');

      const authToken = await apiHelper.getAuthToken();
      
      if (!authToken) {
        console.warn(`No auth token available for ${endpoint}`);
        throw new Error('AUTH0_NOT_READY');
      }
      
      // Route getAllNews to the new news endpoint with AI summary generation
      let apiEndpoint;
      if (endpoint === 'getAllNews') {
        apiEndpoint = `${apiUrl}news/${endpoint}`;
      } else {
        apiEndpoint = `${apiUrl}graph/${endpoint}`;
      }

      const response = await fetch(apiEndpoint, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      });

      // Handle auth errors gracefully during login period
      if (response.status === 401 || response.status === 403) {
        console.warn(`Auth error for ${endpoint}, clearing token cache`);
        // Clear cached token for next request
        await setItemInIndexedDB('authToken', null);
        await setItemInIndexedDB('tokenExpiry', null);
        throw new Error('AUTH_ERROR');
      }
    
      await apiHelper.handleResponse(response);
      return await response.json();
    } catch (err) {
      // Handle Auth0 not ready errors more gracefully
      if (err.message === 'AUTH0_NOT_READY') {
        console.log(`Auth0 not ready for ${endpoint}, will retry when authenticated`);
        // Return a specific error that components can handle
        throw new Error('AUTH0_NOT_READY');
      }
      
      // Handle auth errors
      if (err.message === 'AUTH_ERROR') {
        console.log(`Auth error for ${endpoint}, token expired or invalid`);
        throw new Error('AUTH0_NOT_READY'); // Treat as not ready for retry
      }
      
      console.warn(`Error fetching JSON data from ${endpoint}:`, err.message);
      errorLogger.logApiError(
        `graph/${endpoint}`, 
        err, 
        { method: 'GET' },
        null
      );
      throw err;
    }
  },

  fetchPDFData: async (endpoint) => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      if (!apiUrl) throw new Error('API URL is not defined');
      const authToken = await apiHelper.getAuthToken();
      const newPoint = `${apiUrl}graph/${endpoint}`
      const response = await fetch(`${newPoint}`, {
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      });

      await apiHelper.handleResponse(response);

      return await response.blob();
    } catch (err) {
      console.error('Error fetching PDF data:', err.message);
      throw err;
    }
  },

  fetchJSONFileByNameAndContent: async function (fileName, bypassCache = false) {
    const cacheKey = `jsonFile-${fileName}`;
    const cacheExpiryKey = `${cacheKey}-timestamp`;
    // If bypassCache is false, check the cache
    if (!bypassCache) {
      const cachedData = await getItemFromIndexedDB(cacheKey);
      const cacheTime = await getItemFromIndexedDB(cacheExpiryKey);

      if (cachedData && isCacheValid(cacheTime, expiryTime)) {
        return { fileContent: cachedData };
      }
    }
    // Fetch fresh data if bypassCache is true or cache is invalid
    try {
      const fileData = await this.fetchJSONData(`getFileIdByFileName/${fileName}`);
      if (!fileData.fileId) throw new Error('File ID not found');
      const fileContent = await this.fetchJSONData(`getJSONContent/${fileData.fileId}`);
      // Save the fresh data in IndexedDB for future use
      await setItemInIndexedDB(cacheKey, fileContent);
      await setItemInIndexedDB(cacheExpiryKey, Date.now());

      return { fileContent };
    } catch (err) {
      // Handle Auth0 not ready gracefully during initial load
      if (err.message === 'AUTH0_NOT_READY') {
        console.log(`Auth not ready for fetching ${fileName}, will retry later`);
        throw err; // Let the caller handle the retry
      }
      
      console.warn('Error fetching JSON file by name and content:', err.message);
      throw err;
    }
  },

  /**
   * Fetch JSON file with option to load locally or from API
   * @param {string} fileName - Name of the JSON file to fetch
   * @param {boolean} bypassCache - Whether to bypass cache (for API loading)
   * @param {boolean} useLocalFiles - Whether to load from local data folder
   * @returns {Promise<Object>} - Promise resolving to file content
   */
  fetchJSONFileWithLocalOption: async function (fileName, bypassCache = false, useLocalFiles = false) {
    // Check environment variable for local file loading
    const shouldUseLocalFiles = useLocalFiles || 
      process.env.REACT_APP_USE_LOCAL_FILES === 'true' ||
      process.env.REACT_APP_LOCAL_FILE_MODE === 'true';
    
    if (shouldUseLocalFiles) {
      try {
        // Check if file is available locally
        if (localFileHelper.isFileAvailableLocally(fileName)) {
          return await localFileHelper.loadLocalJSONFile(fileName);
        } else {
          console.warn(`⚠️  File ${fileName} not available locally, falling back to API`);
          return await this.fetchJSONFileByNameAndContent(fileName, bypassCache);
        }
      } catch (error) {
        console.error(`Error loading ${fileName} locally, falling back to API:`, error.message);
        return await this.fetchJSONFileByNameAndContent(fileName, bypassCache);
      }
    } else {
      return await this.fetchJSONFileByNameAndContent(fileName, bypassCache);
    }
  },
  fetchPDFNameAndContentById: async function (listItemId, bypassCache = false) {
    const cacheKey = `jsonFile-${listItemId}`;
    const cacheExpiryKey = `${cacheKey}-timestamp`;
    if (!bypassCache) {
      const cachedData = await getItemFromIndexedDB(cacheKey);
      const cacheTime = await getItemFromIndexedDB(cacheExpiryKey);

      if (cachedData && isCacheValid(cacheTime, expiryTime)) {
        return { fileContent: cachedData };
      }
    }
    try {
      // Fetch file ID by name
      const fileContent = await this.fetchPDFData(`getFileContentByListItemId/${listItemId}`);
      // Cache the fetched PDF content (as blob) and the current timestamp in IndexedDB
      await setItemInIndexedDB(cacheKey, fileContent);
      await setItemInIndexedDB(cacheExpiryKey, Date.now());
      return { fileContent };
    } catch (err) {
      console.error('Error fetching PDF file by name and content:', err.message);
      throw err;
    }
  },

  fetchPDFNameAndContentByTitle: async function (title, bypassCache = false) {
    const cacheKey = `jsonFile-${title}`;
    const cacheExpiryKey = `${cacheKey}-timestamp`;

    if (!bypassCache) {
      const cachedData = await getItemFromIndexedDB(cacheKey);
      const cacheTime = await getItemFromIndexedDB(cacheExpiryKey);

      if (cachedData && isCacheValid(cacheTime, expiryTime)) {
        return { fileContent: cachedData };
      }
    }
    try {
      // Fetch file ID by name
      const fileContent = await this.fetchPDFData(`getFileContentByTitle/${title}`);
      // Cache the fetched PDF content (as blob) and the current timestamp in IndexedDB
      await setItemInIndexedDB(cacheKey, fileContent);
      await setItemInIndexedDB(cacheExpiryKey, Date.now());
      return { fileContent };
    } catch (err) {
      console.error('Error fetching PDF file by name and content:', err.message);
      throw err;
    }
  },

  fetchImageByName: async function (imageName, bypassCache = false) {
    const cacheKey = `image-${imageName}`;
    const cacheExpiryKey = `${cacheKey}-timestamp`;

    if (!bypassCache) {
      const cachedData = await getItemFromIndexedDB(cacheKey);
      const cacheTime = await getItemFromIndexedDB(cacheExpiryKey);

      if (cachedData && isCacheValid(cacheTime, expiryTime)) {
        return { fileContent: cachedData };
      }
    }
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      if (!apiUrl) throw new Error('API URL is not defined');
      const authToken = await apiHelper.getAuthToken();
      const encodedImageName = encodeURIComponent(imageName);
      const response = await fetch(`${apiUrl}graph/getMediaContent/${encodedImageName}`, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Accept': 'image/*'  // Specify we want image content
        }
      });

      await apiHelper.handleResponse(response);
      const blob = await response.blob();

      await setItemInIndexedDB(cacheKey, blob);
      await setItemInIndexedDB(cacheExpiryKey, Date.now());

      return { fileContent: blob };
    } catch (err) {
      console.error('Error fetching image:', err.message);
      throw err;
    }
  },

  fetchVideoByName: async function (videoName, bypassCache = false) {
    const cacheKey = `video-${videoName}`;
    const cacheExpiryKey = `${cacheKey}-timestamp`;

    if (!bypassCache) {
      const cachedData = await getItemFromIndexedDB(cacheKey);
      const cacheTime = await getItemFromIndexedDB(cacheExpiryKey);

      if (cachedData && isCacheValid(cacheTime, expiryTime)) {
        return { fileContent: cachedData };
      }
    }
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      if (!apiUrl) throw new Error('API URL is not defined');
      const authToken = await apiHelper.getAuthToken();
      const encodedVideoName = encodeURIComponent(videoName);
      const response = await fetch(`${apiUrl}graph/getMediaContent/${encodedVideoName}`, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Accept': 'video/*'  // Specify we want video content
        }
      });

      await apiHelper.handleResponse(response);
      const blob = await response.blob();

      await setItemInIndexedDB(cacheKey, blob);
      await setItemInIndexedDB(cacheExpiryKey, Date.now());

      return { fileContent: blob };
    } catch (err) {
      console.error('Error fetching video:', err.message);
      throw err;
    }
  },

  fetchAudioByName: async function (audioName, bypassCache = false) {
    const cacheKey = `audio-${audioName}`;
    const cacheExpiryKey = `${cacheKey}-timestamp`;

    if (!bypassCache) {
      const cachedData = await getItemFromIndexedDB(cacheKey);
      const cacheTime = await getItemFromIndexedDB(cacheExpiryKey);

      if (cachedData && isCacheValid(cacheTime, expiryTime)) {
        return { fileContent: cachedData };
      }
    }
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      if (!apiUrl) throw new Error('API URL is not defined');
      const authToken = await apiHelper.getAuthToken();
      const encodedAudioName = encodeURIComponent(audioName);
      const response = await fetch(`${apiUrl}graph/getMediaContent/${encodedAudioName}`, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Accept': 'audio/*'  // Specify we want audio content
        }
      });

      await apiHelper.handleResponse(response);
      const blob = await response.blob();

      await setItemInIndexedDB(cacheKey, blob);
      await setItemInIndexedDB(cacheExpiryKey, Date.now());

      return { fileContent: blob };
    } catch (err) {
      console.error('Error fetching audio:', err.message);
      throw err;
    }
  },

  createWorkingGroupUpdate: async function (workingGroupId, updateData) {
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      if (!apiUrl) throw new Error('API URL is not defined');

      const authToken = await this.getAuthToken();

      const response = await fetch(`${apiUrl}graph/updateWorkingGroupUpdate/${workingGroupId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          updateDate: updateData.updateDate,
          update: updateData.update,
          updatedBy: updateData.updatedBy
        })
      });

      await this.handleResponse(response);

      return await response.json();
    } catch (err) {
      console.error('Error creating working group update:', err.message);
      throw err;
    }
  },

  addNewWorkingGroupUpdate: async function (updateData) {
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      if (!apiUrl) throw new Error('API URL is not defined');

      const authToken = await this.getAuthToken();

      const response = await fetch(`${apiUrl}graph/createWorkingGroupUpdate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          updateDate: updateData.updateDate,
          update: updateData.update,
          updatedBy: updateData.updatedBy,
          workingGroupNameId: updateData.workingGroupNameId
        })
      });

      await this.handleResponse(response);

      return await response.json();
    } catch (err) {
      console.error('Error adding new working group update:', err.message);
      throw err;
    }
  },

  updateProfileWarning: async function (profileData) {
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      if (!apiUrl) throw new Error('API URL is not defined');
      const authToken = await this.getAuthToken();

      // Get the profile using email
      const currentProfile = await this.getMemberProfileByEmail(profileData.email);

      const requestData = {
        ...currentProfile,
        fieldVisibility: {
          ...currentProfile.fieldVisibility,
          showProfileWarning: false
        }
      };

      const response = await fetch(`${apiUrl}graph/updateProfile/${currentProfile.id}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestData)
      });

      await this.handleResponse(response);
      return await response.json();
    } catch (err) {
      console.error('Error updating regular member profile:', err.message);
      throw err;
    }
  },

  updateProfile: async function (profileData) {
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      if (!apiUrl) throw new Error('API URL is not defined');
      const authToken = await this.getAuthToken();

      // Convert NoOfYrsintheIndustry to string
      const requestData = {
        name: profileData.name,
        linkedInProfile: profileData.linkedInProfile,
        jobTitle: profileData.jobTitle,
        NoOfYrsintheIndustry: profileData.NoOfYrsintheIndustry,
        fieldVisibility: profileData.fieldVisibility,
        bio: profileData.bio,
        workingGroupRole: profileData.workingGroupRole,
        showProfileWarning: profileData.showProfileWarning,
        company: profileData.company,
        memberCohort: profileData.memberCohort
      };

      const memberId = profileData.id;

      const response = await fetch(`${apiUrl}graph/updateProfile/${memberId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestData)
      });

      await this.handleResponse(response);
      return await response.json();
    } catch (err) {
      console.error('Error updating regular member profile:', err.message);
      throw err;
    }
  },

  fetchMediaContent: async function (fileName, type = 'general', bypassCache = false) {
    const cacheKey = `media-${fileName}`;
    const cacheExpiryKey = `${cacheKey}-timestamp`;

    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      if (!apiUrl) throw new Error('API URL is not defined');

      const authToken = await this.getAuthToken();

      const url = `${apiUrl}graph/getMediaContent/${fileName}`;

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      });

      if (!response.ok) {
        console.error(`HTTP error fetching ${fileName}:`, response.status, response.statusText);
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blob = await response.blob();
      return URL.createObjectURL(blob);
    } catch (err) {
      console.error('Error fetching media content:', err.message);
      throw err;
    }
  },

  mediaHandler: {
    getFullPath: function (fileName, type) {
      switch (type) {
        case 'profile':
          return `profile-images/${fileName}`;
        case 'news':
          return `news/${fileName}`;
        case 'gallery':
          return `gallery/${fileName}`;
        default:
          return fileName;
      }
    },

    fetchMedia: async function (fileName, type = 'general', bypassCache = false) {
      const cacheKey = `media-${fileName}-${type}`;
      const cacheExpiryKey = `${cacheKey}-timestamp`;

      // Check cache first unless bypassed
      if (!bypassCache) {
        const cachedData = await getItemFromIndexedDB(cacheKey);
        const cacheTime = await getItemFromIndexedDB(cacheExpiryKey);

        if (cachedData && isCacheValid(cacheTime, expiryTime)) {
          return URL.createObjectURL(cachedData);
        }
      }

      try {
        const apiUrl = process.env.REACT_APP_API_URL;
        if (!apiUrl) throw new Error('API URL is not defined');

        const authToken = await apiHelper.getAuthToken();
        const fullPath = this.getFullPath(fileName, type);

        const response = await fetch(`${apiUrl}graph/getMediaContent/${fullPath}`, {
          headers: {
            'Authorization': `Bearer ${authToken}`
          }
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch media: ${response.status}`);
        }

        const blob = await response.blob();

        // Cache the blob
        await setItemInIndexedDB(cacheKey, blob);
        await setItemInIndexedDB(cacheExpiryKey, Date.now());

        return URL.createObjectURL(blob);
      } catch (err) {
        console.error('Error fetching media:', err.message);
        throw err;
      }
    },

    revokeMediaUrl: function (url) {
      if (url && url.startsWith('blob:')) {
        URL.revokeObjectURL(url);
      }
    },

    // Add a new method to update cache with new image
    updateImageCache: async function (fileName, blob, type = 'profile') {
      const cacheKey = `media-${fileName}-${type}`;
      const cacheExpiryKey = `${cacheKey}-timestamp`;

      // Store the new blob in cache
      await setItemInIndexedDB(cacheKey, blob);
      await setItemInIndexedDB(cacheExpiryKey, Date.now());
    }
  },

  getWorkingGroupMemberProfile: async function (memberId) {
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      if (!apiUrl) throw new Error('API URL is not defined');

      const authToken = await this.getAuthToken();
      const response = await fetch(`${apiUrl}graph/getWorkingGroupMemberProfile/${memberId}`, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      });

      await this.handleResponse(response);
      return await response.json();
    } catch (err) {
      console.error('Error fetching member profile:', err.message);
      throw err;
    }
  },

  getMemberProfileByEmail: async function (email) {
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      if (!apiUrl) throw new Error('API URL is not defined');
      const authToken = await this.getAuthToken();

      if (!authToken) {
        console.warn(`No auth token available for profile: ${email}`);
        throw new Error('AUTH0_NOT_READY');
      }

      const response = await fetch(`${apiUrl}graph/getMemberProfileByEmail/${encodeURIComponent(email)}`, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      });

      // Handle auth errors gracefully during login period
      if (response.status === 401 || response.status === 403) {
        console.warn(`Auth error fetching profile for ${email}, clearing token cache`);
        // Clear cached token for next request
        await setItemInIndexedDB('authToken', null);
        await setItemInIndexedDB('tokenExpiry', null);
        throw new Error('AUTH0_NOT_READY');
      }

      await this.handleResponse(response);
      return await response.json();
    } catch (err) {
      // Handle Auth0 not ready gracefully
      if (err.message === 'AUTH0_NOT_READY') {
        console.log(`Auth not ready for profile ${email}, will retry later`);
        throw err;
      }
      
      console.warn('Error fetching member profile by email:', err.message);
      throw err;
    }
  },

  uploadProfileVideo: async function (videoFile, memberId) {
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      if (!apiUrl) throw new Error('API URL is not defined');

      const authToken = await this.getAuthToken();

      const formData = new FormData();
      formData.append('video', videoFile);

      const response = await fetch(`${apiUrl}graph/uploadProfileVideo/${memberId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
        },
        body: formData
      });

      await this.handleResponse(response);
      return await response.json();
    } catch (err) {
      console.error('Error uploading profile video:', err.message);
      throw err;
    }
  },

  deleteProfileVideo: async function (memberId) {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}graph/deleteProfileVideo/${memberId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${await this.getAuthToken()}`
        }
      });

   

      if (!response.ok) {
        throw new Error('Failed to delete video');
      }

      return await response.json();
    } catch (error) {
      console.error('Error deleting profile video:', error);
      throw error;
    }
  },

  uploadMemberProfileImage: async function (file, memberId) {
    const formData = new FormData();
    formData.append('image', file);

    const response = await fetch(
      `${process.env.REACT_APP_API_URL}graph/uploadMemberProfileImage/${memberId}`,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${await this.getAuthToken()}`
        },
        body: formData
      }
    );

    if (!response.ok) {
      throw new Error('Failed to upload image');
    }

    return await response.json();
  },

  deleteProfileImage: async function (memberId) {
    try {
        const apiUrl = process.env.REACT_APP_API_URL;
        if (!apiUrl) throw new Error('API URL is not defined');
        const authToken = await this.getAuthToken();

        const response = await fetch(`${apiUrl}graph/deleteProfileImage/${memberId}`, {
            method: 'DELETE',
            headers: {
                'Authorization': `Bearer ${authToken}`
            }
        });

        await this.handleResponse(response);
        return await response.json();
    } catch (err) {
        console.error('Error deleting profile image:', err);
        throw err;
    }
  },

  checkIfWorkingGroupMember: async function (email) {
    try {
        const apiUrl = process.env.REACT_APP_API_URL;
        if (!apiUrl) throw new Error('API URL is not defined');
        const authToken = await this.getAuthToken();

        const response = await fetch(`${apiUrl}graph/checkWorkingGroupMember/${encodeURIComponent(email)}`, {
            headers: {
                'Authorization': `Bearer ${authToken}`,
                'Content-Type': 'application/json'
            }
        });

        await this.handleResponse(response);
        const result = await response.json(); 

        return result;
    } catch (err) {
        console.error('Error checking working group member status:', err);
        throw err;
    }
  },

  createProfile: async function (profileData) {
    try {
        const authToken = await apiHelper.getAuthToken();
        const response = await fetch(`${process.env.REACT_APP_API_URL}graph/createProfile`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${authToken}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                email: profileData.email,
                name: profileData.name,
                fieldVisibility: {
                    email: true,
                    linkedIn: true,
                    jobTitle: true
                }
            })
        });

        if (!response.ok) {
            throw new Error(`Failed to create profile: ${response.statusText}`);
        }

        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Error in createProfile:', error);
        throw error;
    }
  },

  getProfileImageByEmail: async function (email) {
    try {
        // First get the profile to get the photo filename
        const profile = await this.getMemberProfileByEmail(email);
        if (!profile?.photo) {
            return null;
        }

        const apiUrl = process.env.REACT_APP_API_URL;
        if (!apiUrl) throw new Error('API URL is not defined');
        const authToken = await this.getAuthToken();

        // Now get the actual image using the photo filename
        const response = await fetch(`${apiUrl}graph/getMediaContent/profile-images/${profile.photo}`, {
            headers: {
                'Authorization': `Bearer ${authToken}`
            }
        });

        if (!response.ok) {
            if (response.status === 404) {
                return null;
            }
            throw new Error(`Failed to fetch image: ${response.status}`);
        }

        const blob = await response.blob();
        return URL.createObjectURL(blob);
    } catch (err) {
        console.error('Error fetching profile image:', err);
        return null;
    }
  },
  // Qdrant Search
  fetchQdrantSearch: async function (query, page = 1, sortBy = 'relevance', sortOrder = 'desc', type = 'all') {
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      if (!apiUrl) throw new Error('API URL is not defined');
      const authToken = await apiHelper.getAuthToken();
      const response = await fetch(`${apiUrl}qdrant/search`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          query,
          page,
          sortBy,
          sortOrder,
          limit: 10,
          type: type !== 'all' ? type : undefined
        }),
      });

      if (!response.ok) {
        throw new Error('Search failed');
      }

      return await response.json();
    } catch (error) {
      console.error('Search error:', error);
      throw error;
    }
  },
  // Qdrant Index
  indexQdrantDocument: async function(text, metadata = {}, documentId) {
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      if (!apiUrl) throw new Error('API URL is not defined');
      if (!documentId) throw new Error('Document ID is required');
      const authToken = await apiHelper.getAuthToken();
      const response = await fetch(`${apiUrl}qdrant/index`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({ text, metadata, documentId })
      });
  
      await apiHelper.handleResponse(response);
      return await response.json();
    } catch (err) {
      console.error('Error indexing document into Qdrant:', err.message);
      throw err;
    }
  },

  // Qdrant Bulk Index
  fetchQdrantBulkIndex: async function(year) {
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      if (!apiUrl) throw new Error('API URL is not defined');
      const authToken = await apiHelper.getAuthToken();
      const response = await fetch(`${apiUrl}qdrant/bulkIndex`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({ year })
      });
  
      await apiHelper.handleResponse(response);
      return await response.json();
    } catch (err) {
      console.error('Error bulk indexing documents into Qdrant:', err.message);
      throw err;
    }
  },

  // Update chat message rating
  updateChatRating: async function (messageId, rating, userEmail, listId) {
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      if (!apiUrl) throw new Error('API URL is not defined');
      const authToken = await apiHelper.getAuthToken();
      const response = await fetch(`${apiUrl}daire/update-rating`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({ messageId, rating, userEmail, listId })
      });

      await apiHelper.handleResponse(response);
      return await response.json();
    } catch (err) {
      console.error('Error updating chat rating:', err.message);
      throw err;
    }
  },

  // ===== News Repository File Mappings =====
  // Get repository file mappings for a specific news article
  getNewsRepositoryMappings: async function (articleId) {
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      if (!apiUrl) throw new Error('API URL is not defined');
      const authToken = await this.getAuthToken();
      
      const response = await fetch(`${apiUrl}graph/getNewsRepositoryMappings/${articleId}`, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      });

      await this.handleResponse(response);
      const result = await response.json();
      
      return result;
    } catch (err) {
      console.error('Error fetching news repository mappings:', err);
      throw err;
    }
  },

  // Get all repository file mappings (for debugging/admin)
  getAllNewsRepositoryMappings: async function () {
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      if (!apiUrl) throw new Error('API URL is not defined');
      const authToken = await this.getAuthToken();
      
      const response = await fetch(`${apiUrl}graph/getAllNewsRepositoryMappings`, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      });

      await this.handleResponse(response);
      const result = await response.json();
      
      return result;
    } catch (err) {
      console.error('Error fetching all news repository mappings:', err);
      throw err;
    }
  },

  // Create a new repository file mapping
  createNewsRepositoryMapping: async function (mappingData) {
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      if (!apiUrl) throw new Error('API URL is not defined');
      const authToken = await this.getAuthToken();
      
      const response = await fetch(`${apiUrl}graph/createNewsRepositoryMapping`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(mappingData)
      });

      await this.handleResponse(response);
      const result = await response.json();
      
      return result;
    } catch (err) {
      console.error('Error creating news repository mapping:', err);
      throw err;
    }
  },

  // ===== Unified File Route for Backward Compatibility =====
  // Get file by ID (supports both legacy GUIDs and new Repository IDs)
  getFileById: async function (fileId) {
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      if (!apiUrl) throw new Error('API URL is not defined');
      const authToken = await this.getAuthToken();
      
      const response = await fetch(`${apiUrl}graph/file?id=${encodeURIComponent(fileId)}`, {
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      });

      if (!response.ok) {
        // Emit API error event for health monitoring
        if (response.status === 401 || response.status === 403) {
          window.dispatchEvent(new CustomEvent('apiAuthError', {
            detail: { status: response.status, url: response.url }
          }));
        }
        
        if (response.status === 404) {
          throw new Error('File not found');
        }
        throw new Error(`Failed to fetch file: ${response.status} ${response.statusText}`);
      }

      // Return the blob for file content
      const blob = await response.blob();
      return {
        blob: blob,
        contentType: response.headers.get('content-type'),
        filename: this.getFilenameFromResponse(response)
      };
    } catch (err) {
      console.error('Error fetching file by ID:', err);
      throw err;
    }
  },

  // Helper to extract filename from response headers
  getFilenameFromResponse: function (response) {
    const contentDisposition = response.headers.get('content-disposition');
    if (contentDisposition) {
      const match = contentDisposition.match(/filename="(.+)"/);
      if (match) {
        return match[1];
      }
    }
    return 'document';
  },

  // Test the unified file route with a specific ID
  testUnifiedFileRoute: async function (fileId) {
    try {
      const result = await this.getFileById(fileId);
      
      return result;
    } catch (err) {
      console.error('Unified file route test failed:', err);
      throw err;
    }
  },

  // ===== User Activity Tracking =====
  // Log user activity (login, page visits, etc.)
  logUserActivity: async function (userEmail, activityType, activityDescription = '') {
    // Skip logging in development mode
    if (process.env.NODE_ENV === 'development') {
      return { success: true, skipped: true, reason: 'Development mode' };
    }
    
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      if (!apiUrl) throw new Error('API URL is not defined');
      
      const authToken = await this.getAuthToken();
      
      const response = await fetch(`${apiUrl}graph/logPortalUserActivity`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          userEmail,
          activityType,
          activityDescription
        })
      });

      await this.handleResponse(response);
      const result = await response.json();
      
      return result;
    } catch (err) {
      // Log the error for monitoring
      errorLogger.logActivityError(err, {
        userEmail,
        activityType,
        activityDescription
      });
      
      // Don't throw error for activity tracking to avoid disrupting user experience
      return { success: false, error: err.message };
    }
  },

  // Specific helper methods for common activity types
  logUserLogin: async function (userEmail) {
    return await this.logUserActivity(userEmail, 'LOGIN', 'User successfully logged in');
  },

  logPageVisit: async function (userEmail, pagePath, pageTitle = '') {
    const description = pageTitle ? `Visited: ${pageTitle} (${pagePath})` : `Visited: ${pagePath}`;
    return await this.logUserActivity(userEmail, 'PAGE_VISIT', description);
  },

  logModalOpen: async function (userEmail, modalType, title = '') {
    const description = title ? `Opened ${modalType}: ${title}` : `Opened ${modalType}`;
    return await this.logUserActivity(userEmail, 'MODAL_OPEN', description);
  },

  logUserAction: async function (userEmail, action, details = '') {
    return await this.logUserActivity(userEmail, 'USER_ACTION', `${action}: ${details}`);
  },

  /**
   * Submit user feedback to SharePoint PortalFeedback list
   * 
   * @param {Object} feedbackData - The feedback data to submit
   * @param {string} feedbackData.email - User's email address
   * @param {string} feedbackData.page - Current page name
   * @param {string} feedbackData.rating - Rating from 1-5
   * @param {string} feedbackData.overAllImpression - Overall impression feedback
   * @param {string} feedbackData.overAllPerformance - Performance feedback
   * @param {string} [feedbackData.comments] - Optional additional comments
   * @returns {Promise<Object>} Response from the feedback submission API
   * @throws {Error} When feedback submission fails
   */
  submitFeedback: async function (feedbackData) {
    try {
      const token = await this.getAuthToken();
      
      if (!token) {
        throw new Error('Authentication token not available');
      }

             // Validate required fields on frontend too
       const { email, page, rating, overAllImpression, overAllPerformance } = feedbackData;
       
       if (!email || !page || !rating || !overAllImpression || !overAllPerformance) {
         throw new Error('Missing required fields: email, page, rating, overAllImpression, and overAllPerformance are required');
       }

       // Ensure email is not a placeholder/fallback
       if (email.includes('anonymous') || email.includes('example.com')) {
         throw new Error('Valid user email required for feedback submission');
       }

      // Validate rating
      const ratingNum = parseInt(rating);
      if (isNaN(ratingNum) || ratingNum < 1 || ratingNum > 5) {
        throw new Error('Rating must be a number between 1 and 5');
      }

      

      const response = await fetch(`${process.env.REACT_APP_API_URL}graph/submitFeedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(feedbackData)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }

             const result = await response.json();

      // Log the feedback submission as user activity
      try {
        await this.logUserActivity(
          email, 
          'FEEDBACK_SUBMITTED', 
          `Page: ${page}, Rating: ${rating}/5, Timestamp: ${new Date().toISOString()}`
        );
      } catch (logError) {
        console.warn('Failed to log feedback activity:', logError.message);
        // Don't fail the feedback submission if logging fails
      }

      return result;

         } catch (error) {
      
      // Enhance error message for better user experience
      if (error.message.includes('401') || error.message.includes('403')) {
        throw new Error('Authentication failed. Please try logging out and back in.');
      }
      
      if (error.message.includes('404')) {
        throw new Error('Feedback service not available. Please try again later.');
      }
      
      if (error.message.includes('400')) {
        throw new Error('Invalid feedback data. Please check your input and try again.');
      }

      // Re-throw the original error if it already has a user-friendly message
      if (error.message && !error.message.includes('HTTP')) {
        throw error;
      }

             // Generic error for unexpected issues
       throw new Error('Failed to submit feedback. Please try again later.');
     }
   },

   /**
    * Get notifications for the current user
    * 
    * @param {string} userEmail - User's email address
    * @returns {Promise<Object>} Response with notifications array and unread count
    * @throws {Error} When fetching notifications fails
    */
   getNotifications: async function (userEmail) {
     try {
       const token = await this.getAuthToken();
       
       if (!token) {
         console.warn('No auth token available for notifications');
         return { success: false, notifications: [], unreadCount: 0 };
       }

       if (!userEmail) {
         console.warn('No user email provided for notifications');
         return { success: false, notifications: [], unreadCount: 0 };
       }

       const response = await fetch(`${process.env.REACT_APP_API_URL}graph/getNotifications?email=${encodeURIComponent(userEmail)}`, {
         method: 'GET',
         headers: {
           'Authorization': `Bearer ${token}`
         }
       });

       // Handle auth errors gracefully during login period
       if (response.status === 401 || response.status === 403) {
         console.warn('Auth error fetching notifications, token may not be ready yet');
         // Clear cached token for next request
         await setItemInIndexedDB('authToken', null);
         await setItemInIndexedDB('tokenExpiry', null);
         return { success: false, notifications: [], unreadCount: 0 };
       }

       if (!response.ok) {
         const errorData = await response.json().catch(() => ({}));
         console.warn('Error fetching notifications:', errorData);
         return { success: false, notifications: [], unreadCount: 0 };
       }

       const result = await response.json();
       return result;

     } catch (error) {
       console.warn('Error fetching notifications:', error.message);
       
       // Don't throw errors during Auth0 initialization
       if (error.message === 'AUTH0_NOT_READY') {
         return { success: false, notifications: [], unreadCount: 0 };
       }
       
       // Return empty result instead of throwing
       return { success: false, notifications: [], unreadCount: 0 };
     }
   },

   /**
    * Mark a notification as read
    * 
    * @param {string} notificationId - ID of the notification to mark as read
    * @param {string} userEmail - User's email address
    * @returns {Promise<Object>} Response confirming the notification was marked as read
    * @throws {Error} When marking notification as read fails
    */
   markNotificationRead: async function (notificationId, userEmail) {
     try {
       const token = await this.getAuthToken();
       
       if (!token) {
         throw new Error('Authentication token not available');
       }

       if (!notificationId) {
         throw new Error('Notification ID is required');
       }

       if (!userEmail) {
         throw new Error('User email is required');
       }

       const response = await fetch(`${process.env.REACT_APP_API_URL}graph/markNotificationRead`, {
         method: 'POST',
         headers: {
           'Content-Type': 'application/json',
           'Authorization': `Bearer ${token}`
         },
         body: JSON.stringify({ notificationId, userEmail })
       });

       if (!response.ok) {
         const errorData = await response.json().catch(() => ({}));
         throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
       }

       const result = await response.json();
       return result;

     } catch (error) {
       console.error('Error marking notification as read:', error);
       
       if (error.message.includes('401') || error.message.includes('403')) {
         throw new Error('Authentication failed. Please try logging out and back in.');
       }
       
       throw new Error('Failed to mark notification as read. Please try again later.');
     }
   },

   /**
    * Mark a notification as unread
    * 
    * @param {string} notificationId - ID of the notification to mark as unread
    * @param {string} userEmail - User's email address
    * @returns {Promise<Object>} Response confirming the notification was marked as unread
    * @throws {Error} When marking notification as unread fails
    */
   markNotificationUnread: async function (notificationId, userEmail) {
     try {
       const token = await this.getAuthToken();
       
       if (!token) {
         throw new Error('Authentication token not available');
       }

       if (!notificationId) {
         throw new Error('Notification ID is required');
       }

       if (!userEmail) {
         throw new Error('User email is required');
       }

       const response = await fetch(`${process.env.REACT_APP_API_URL}graph/markNotificationUnread`, {
         method: 'POST',
         headers: {
           'Content-Type': 'application/json',
           'Authorization': `Bearer ${token}`
         },
         body: JSON.stringify({ notificationId, userEmail })
       });

       if (!response.ok) {
         const errorData = await response.json().catch(() => ({}));
         throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
       }

       const result = await response.json();
       return result;

     } catch (error) {
       console.error('Error marking notification as unread:', error);
       
       if (error.message.includes('401') || error.message.includes('403')) {
         throw new Error('Authentication failed. Please try logging out and back in.');
       }
       
       throw new Error('Failed to mark notification as unread. Please try again later.');
     }
   },

   /**
    * Get unread notification count for badge display
    * 
    * @param {string} userEmail - User's email address
    * @returns {Promise<Object>} Response with unread count and total count
    * @throws {Error} When fetching notification count fails
    */
   getNotificationCount: async function (userEmail) {
     try {
       const token = await this.getAuthToken();
       
       if (!token) {
         console.warn('No auth token available for notification count');
         return { success: true, unreadCount: 0, totalCount: 0 };
       }

       if (!userEmail) {
         console.warn('No user email provided for notification count');
         return { success: true, unreadCount: 0, totalCount: 0 };
       }

       const response = await fetch(`${process.env.REACT_APP_API_URL}graph/getNotificationCount?email=${encodeURIComponent(userEmail)}`, {
         method: 'GET',
         headers: {
           'Authorization': `Bearer ${token}`
         }
       });

       // For notification count, handle auth errors gracefully without retry
       if (response.status === 401 || response.status === 403) {
         console.warn('Auth error fetching notification count, clearing token cache');
         // Clear cached token for next request
         await setItemInIndexedDB('authToken', null);
         await setItemInIndexedDB('tokenExpiry', null);
         // Return 0 count instead of throwing error
         return { success: true, unreadCount: 0, totalCount: 0 };
       }

       if (!response.ok) {
         const errorData = await response.json().catch(() => ({}));
         console.warn('Error fetching notification count:', errorData);
         // Return 0 for errors to avoid breaking the UI
         return { success: true, unreadCount: 0, totalCount: 0 };
       }

       const result = await response.json();
       return result;

     } catch (error) {
       console.warn('Error fetching notification count:', error.message);
       // Return 0 for errors to avoid breaking the UI
       return { success: true, unreadCount: 0, totalCount: 0 };
     }
   },
};

export const initializeAPI = apiHelper.initialize;
export default apiHelper;
